import { Button } from '@neo/index'
import { ArrowLeft } from 'lucide-react'
import type { PropsWithChildren } from 'react'
import { ErrorBoundary } from '../components/error-boundary'
import type { SideSheet } from '../components/side-sheet'

interface Props extends PropsWithChildren {
  title: string | React.ReactElement
  subTitle?: string | React.ReactElement
  subTitleComponent?: string | React.ReactElement
  handleGoBack?: () => void
  /* The sheet component to be opened on the right side of the screen */
  sheet?: React.ReactElement<typeof SideSheet>
  /* Custom className props for flexible styling */
  containerClassName?: string
  headerClassName?: string
  contentClassName?: string
  // Shown at the header's right edge, centred on the title and subtitle.
  headerAside?: React.ReactNode
}
export const TableWithSheetLayout: React.FC<Props> = ({
  title,
  children,
  sheet,
  handleGoBack,
  containerClassName = 'px-6 py-5 h-full overflow-hidden flex flex-col pb-2',
  headerClassName = 'mb-6',
  contentClassName = 'table-layout flex flex-col flex-1 min-h-0',
  subTitle,
  headerAside,
}) => {
  const heading = (
    <>
      {handleGoBack && (
        <Button onClick={handleGoBack} variant="ghost" size="icon" className="h-6 w-6">
          <ArrowLeft width={24} height={24} />
        </Button>
      )}
      {title && <h1 className="text-h3-500">{title}</h1>}
      {subTitle && <div className="font-inter text-s-400 text-secondary">{subTitle}</div>}
    </>
  )
  return (
    <div className={containerClassName}>
      <ErrorBoundary>
        <div className={headerClassName}>
          {headerAside ? (
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">{heading}</div>
              {headerAside}
            </div>
          ) : (
            heading
          )}
        </div>
        {/* Commented out the subtitle */}
        {/* <p className="text-muted-foreground text-p-400 pb-5">{subTitle}</p> */}
        <div className={contentClassName}>{children}</div>

        {/* The sheet is rendered here */}
        {sheet && <div id="side-sheet">{sheet}</div>}
      </ErrorBoundary>
    </div>
  )
}

export const TableWithCustomSheetLayout: React.FC<Props> = ({
  title,
  children,
  sheet,
  handleGoBack,
  subTitleComponent,
  containerClassName = 'px-6 py-4 h-full overflow-hidden flex flex-col pb-2',
  headerClassName = 'flex items-center gap-2 mb-6',
  contentClassName = 'table-layout flex flex-col flex-1 min-h-0',
}) => (
  <div className={containerClassName}>
    <ErrorBoundary>
      <div className={headerClassName}>
        {handleGoBack && (
          <Button onClick={handleGoBack} variant="ghost" size="icon" className="h-6 w-6">
            <ArrowLeft width={24} height={24} />
          </Button>
        )}
        <div className="flex flex-col">
          <h1 className="text-h3-500">{title}</h1>
          {subTitleComponent && (
            <span className="font-inter text-xs-400 align-middle table-layout text-secondary">
              {subTitleComponent}
            </span>
          )}
        </div>
      </div>
      <div className={contentClassName}>{children}</div>

      {/* The sheet is rendered here */}
      {sheet && <div id="side-sheet">{sheet}</div>}
    </ErrorBoundary>
  </div>
)
